import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import { TIER_LIMITS, TIER_META } from '../subscriptions';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { getToken } from '../api';

/* Logged-in visitors go straight to the in-app upgrade page; logged-out
   visitors go to sign-up to start a trial on the chosen plan. */
function ctaTarget(tier, isAuth) {
  return isAuth ? '/billing' : tier.ctaTo;
}

/* ─── Design tokens ──────────────────────────────────────── */
const INK  = '#0A1F4A';
const INK2 = '#3D4F78';
const INK3 = '#7883A2';
const BG   = '#ECEEF3';
const SURF = '#FFFFFF';
const RULE = '#D6DAE4';
const ACC  = '#FF7A3D';

/* ─── Paid tier display config ───────────────────────────── */
const TIERS = [
  {
    key: 'tier1',
    name: TIER_META.tier1.label,
    price: `€${TIER_META.tier1.price_eur}`,
    period: TIER_META.tier1.period,
    highlight: false,
    cta: 'Get started',
    ctaTo: '/signup?plan=tier1',
    description: 'For solo businesses targeting one country.',
    features: [
      { text: 'Unlimited matches', included: true },
      { text: 'AI relevance scoring (0–100)', included: true },
      { text: '1 product profile', included: true },
      { text: 'Email alerts for new matches', included: true },
      { text: '1 country', included: true },
      { text: 'Daily match refresh', included: true },
      { text: 'API access (CRM integration)', included: false },
      { text: 'Search tool — browse all tenders', included: false },
      { text: 'CSV export', included: false },
    ],
  },
  {
    key: 'tier2',
    name: TIER_META.tier2.label,
    price: `€${TIER_META.tier2.price_eur}`,
    period: TIER_META.tier2.period,
    highlight: true,
    cta: 'Get started →',
    ctaTo: '/signup?plan=tier2',
    description: 'For businesses expanding across the EU.',
    features: [
      { text: 'Unlimited matches', included: true },
      { text: 'AI relevance scoring (0–100)', included: true },
      { text: `${TIER_LIMITS.tier2.max_profiles} product profiles`, included: true },
      { text: 'Email alerts for new matches', included: true },
      { text: 'All TED countries', included: true },
      { text: 'Daily match refresh', included: true },
      { text: 'API access (CRM integration)', included: true },
      { text: 'Search tool — browse all tenders', included: true },
      { text: 'CSV export', included: false },
    ],
  },
  {
    key: 'tier3',
    name: TIER_META.tier3.label,
    price: `€${TIER_META.tier3.price_eur}`,
    period: TIER_META.tier3.period,
    highlight: false,
    cta: 'Get started',
    ctaTo: '/signup?plan=tier3',
    description: 'For agencies and larger procurement teams.',
    features: [
      { text: 'Unlimited matches', included: true },
      { text: 'AI relevance scoring (0–100)', included: true },
      { text: `${TIER_LIMITS.tier3.max_profiles} product profiles`, included: true },
      { text: 'Email alerts for new matches', included: true },
      { text: 'All TED countries', included: true },
      { text: 'Daily match refresh', included: true },
      { text: 'API access (CRM integration)', included: true },
      { text: 'Search tool — browse all tenders', included: true },
      { text: 'CSV export', included: true },
    ],
  },
];

const FAQ = [
  {
    q: 'How is the relevance score calculated?',
    a: 'A language model trained on 6M historical tenders reads each notice and weighs scope, fit, value band, incumbent risk, and geographic match against your company profile. Every score ships with a one-line explanation — no black box.',
  },
  {
    q: 'Which countries do you cover?',
    a: "All 27 EU member states, the UK, Norway, and Switzerland. National journals, regional portals, hospital and transport authority feeds included on Professional and above.",
  },
  {
    q: 'Can I integrate with my CRM?',
    a: 'Professional and above include REST API access so you can push matches directly into HubSpot, Salesforce, Pipedrive, or any CRM that accepts a webhook.',
  },
  {
    q: 'What happens when my free trial expires?',
    a: "Your account is paused after 14 days. Your profile and match history stay intact — just pick a paid plan to reactivate.",
  },
  {
    q: 'Do you offer discounts for agencies?',
    a: 'Yes — for agencies managing multiple client profiles, reach out to sales@jyto.eu and we can discuss volume pricing.',
  },
];

function Kicker({ children }) {
  return (
    <span style={{
      display: 'inline-block',
      fontFamily: '"JetBrains Mono", monospace',
      fontSize: 11, fontWeight: 600,
      letterSpacing: '0.14em', textTransform: 'uppercase',
      padding: '4px 10px',
      background: INK, color: ACC,
      borderRadius: 4,
      marginBottom: 16,
    }}>
      {children}
    </span>
  );
}

function PlanCard({ tier, isAuth }) {
  const hl = tier.highlight;

  const cardStyle = hl
    ? {
        background: INK,
        color: BG,
        borderRadius: 0,
        padding: '40px 22px 36px',
        display: 'flex', flexDirection: 'column', gap: 16,
        position: 'relative',
        zIndex: 2,
        flex: 1,
        boxShadow: `0 20px 40px -8px rgba(10,31,74,0.45)`,
      }
    : {
        background: SURF,
        borderRadius: 0,
        padding: '32px 22px 28px',
        display: 'flex', flexDirection: 'column', gap: 16,
        position: 'relative',
        flex: 1,
      };

  const nameStyle = {
    fontFamily: '"JetBrains Mono", monospace',
    fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase',
    color: hl ? ACC : INK3,
  };

  const priceStyle = {
    fontSize: 44,
    fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1,
    color: hl ? BG : INK,
  };

  const unitStyle = {
    fontSize: 12, color: hl ? 'rgba(236,238,243,0.55)' : INK3,
    fontFamily: '"JetBrains Mono", monospace', marginLeft: 6,
  };

  const descStyle = {
    fontSize: 13, color: hl ? 'rgba(236,238,243,0.7)' : INK2,
    minHeight: 44, margin: 0,
  };

  const dividerStyle = {
    borderTop: `1px solid ${hl ? 'rgba(255,255,255,0.12)' : RULE}`,
    paddingTop: 16,
  };

  const ctaStyle = hl
    ? {
        display: 'block', textAlign: 'center',
        background: ACC, color: '#fff',
        fontWeight: 600, fontSize: 14,
        padding: '11px 16px', borderRadius: 10,
        textDecoration: 'none',
        boxShadow: '0 4px 0 0 rgba(0,0,0,0.25)',
        marginTop: 'auto',
      }
    : {
        display: 'block', textAlign: 'center',
        background: 'transparent', color: INK,
        border: `1px solid ${INK}`,
        fontWeight: 600, fontSize: 14,
        padding: '11px 16px', borderRadius: 10,
        textDecoration: 'none',
        marginTop: 'auto',
      };

  return (
    <div style={cardStyle}>
      {hl && (
        <span style={{
          position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
          background: ACC, color: '#fff',
          fontFamily: '"JetBrains Mono", monospace',
          fontSize: 11, fontWeight: 600, letterSpacing: '0.14em',
          padding: '6px 14px', borderRadius: 999,
          border: `1px solid ${INK}`,
          whiteSpace: 'nowrap',
        }}>
          MOST POPULAR
        </span>
      )}

      <div style={nameStyle}>{tier.name}</div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
        <span style={priceStyle}>{tier.price}</span>
        {tier.period && (
          <span style={unitStyle}>/ {tier.period}</span>
        )}
      </div>

      <p style={descStyle}>{tier.description}</p>

      <Link to={ctaTarget(tier, isAuth)} style={ctaStyle}>{tier.cta}</Link>

      <div style={dividerStyle}>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {tier.features.map((f) => (
            <li key={f.text} style={{
              display: 'flex', gap: 8, alignItems: 'flex-start',
              fontSize: 13,
              color: f.included
                ? (hl ? 'rgba(236,238,243,0.85)' : INK2)
                : (hl ? 'rgba(236,238,243,0.3)' : INK3),
            }}>
              <span style={{
                width: 14, height: 14, flexShrink: 0, marginTop: 3,
                borderRadius: 4,
                background: f.included ? ACC : 'transparent',
                boxShadow: f.included ? 'none' : `inset 0 0 0 1px ${RULE}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 9, color: '#fff', fontWeight: 700,
              }}>
                {f.included ? '✓' : ''}
              </span>
              {f.text}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function TrialBanner({ isMobile }) {
  return (
    <div style={{
      display: 'flex',
      flexDirection: isMobile ? 'column' : 'row',
      alignItems: isMobile ? 'flex-start' : 'center',
      justifyContent: 'space-between',
      gap: isMobile ? 16 : 24,
      background: SURF,
      border: `1px solid ${RULE}`,
      borderLeft: `4px solid ${ACC}`,
      borderRadius: 14,
      padding: isMobile ? '20px 20px' : '20px 28px',
      marginBottom: 36,
    }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 700, color: INK, marginBottom: 4 }}>
          Not sure yet? Try Jyto free for 14 days.
        </div>
        <div style={{ fontSize: 13, color: INK2 }}>
          No credit card required. No commitment. See your first matches by tomorrow morning.
        </div>
      </div>
      <Link to="/signup" style={{
        flexShrink: 0,
        display: 'inline-block',
        background: INK,
        color: BG,
        fontWeight: 600,
        fontSize: 13,
        padding: '10px 22px',
        borderRadius: 10,
        textDecoration: 'none',
        whiteSpace: 'nowrap',
      }}>
        Start free trial →
      </Link>
    </div>
  );
}

export default function Pricing() {
  const width = useWindowWidth();
  const isMobile = width < 700;
  const isAuth = !!getToken();

  return (
    <div style={{ background: BG, minHeight: '100vh' }}>

      {/* Hero */}
      <div style={{
        background: INK,
        padding: isMobile ? '32px 24px 40px' : '36px 24px 44px',
        textAlign: 'center',
        position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: 600, height: 300, borderRadius: '50%', background: ACC, opacity: 0.07, filter: 'blur(60px)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Kicker>Pricing</Kicker>
          <h1 style={{
            fontSize: isMobile ? 36 : 52,
            fontWeight: 700, color: BG,
            letterSpacing: '-0.03em', lineHeight: 1.05,
            marginBottom: 16,
          }}>
            Find the right plan for your pipeline.
          </h1>
          <p style={{ fontSize: 18, color: 'rgba(236,238,243,0.65)', maxWidth: 480, margin: '0 auto', lineHeight: 1.6 }}>
            Pick a plan and get full access from day one. Cancel any time.
          </p>
        </div>
      </div>

      {/* Pricing grid */}
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: isMobile ? '32px 16px' : '36px 24px' }}>
        <TrialBanner isMobile={isMobile} />

        {isMobile ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {TIERS.map((tier) => (
              <div key={tier.key} style={{
                background: tier.highlight ? INK : SURF,
                border: `1px solid ${tier.highlight ? INK : RULE}`,
                borderRadius: 16,
                padding: '28px 24px',
                position: 'relative',
              }}>
                {tier.highlight && (
                  <span style={{
                    position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)',
                    background: ACC, color: '#fff',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: 10, fontWeight: 600, letterSpacing: '0.12em',
                    padding: '5px 12px', borderRadius: 999,
                    whiteSpace: 'nowrap',
                  }}>MOST POPULAR</span>
                )}
                <PlanCard tier={tier} isAuth={isAuth} />
              </div>
            ))}
          </div>
        ) : (
          <div style={{ paddingTop: 20 }}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              border: `1px solid ${INK}`,
              borderRadius: 20,
              overflow: 'visible',
              background: SURF,
            }}>

              {/* ── Name row ── */}
              {TIERS.map((tier, i) => (
                <div key={`name-${i}`} style={{
                  background: tier.highlight ? INK : SURF,
                  padding: '32px 22px 8px',
                  borderRight: i < TIERS.length - 1 && !tier.highlight ? `1px solid ${RULE}` : 'none',
                  borderLeft: i > 0 && TIERS[i - 1].highlight ? `1px solid ${RULE}` : 'none',
                  borderRadius: i === 0 ? '19px 0 0 0' : i === TIERS.length - 1 ? '0 19px 0 0' : 0,
                  overflow: i !== 1 ? 'hidden' : 'visible',
                  position: 'relative',
                }}>
                  {tier.highlight && (
                    <span style={{
                      position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                      background: ACC, color: '#fff',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontSize: 11, fontWeight: 600, letterSpacing: '0.14em',
                      padding: '6px 14px', borderRadius: 999,
                      border: `1px solid ${INK}`,
                      whiteSpace: 'nowrap',
                    }}>MOST POPULAR</span>
                  )}
                  <div style={{ fontFamily: '"JetBrains Mono", monospace', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: tier.highlight ? ACC : INK3 }}>
                    {tier.name}
                  </div>
                </div>
              ))}

              {/* ── Price row ── */}
              {TIERS.map((tier, i) => (
                <div key={`price-${i}`} style={{
                  background: tier.highlight ? INK : SURF,
                  padding: '10px 22px 0',
                  borderRight: i < TIERS.length - 1 && !tier.highlight ? `1px solid ${RULE}` : 'none',
                  borderLeft: i > 0 && TIERS[i - 1].highlight ? `1px solid ${RULE}` : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                    <span style={{ fontSize: 44, fontWeight: 700, letterSpacing: '-0.03em', lineHeight: 1, color: tier.highlight ? BG : INK }}>{tier.price}</span>
                    <span style={{ fontSize: 12, color: tier.highlight ? 'rgba(236,238,243,0.55)' : INK3, fontFamily: '"JetBrains Mono", monospace', marginLeft: 6 }}>/ {tier.period}</span>
                  </div>
                </div>
              ))}

              {/* ── Description row ── */}
              {TIERS.map((tier, i) => (
                <div key={`desc-${i}`} style={{
                  background: tier.highlight ? INK : SURF,
                  padding: '10px 22px 0',
                  borderRight: i < TIERS.length - 1 && !tier.highlight ? `1px solid ${RULE}` : 'none',
                  borderLeft: i > 0 && TIERS[i - 1].highlight ? `1px solid ${RULE}` : 'none',
                }}>
                  <p style={{ fontSize: 13, color: tier.highlight ? 'rgba(236,238,243,0.7)' : INK2, margin: 0 }}>{tier.description}</p>
                </div>
              ))}

              {/* ── CTA row ── */}
              {TIERS.map((tier, i) => (
                <div key={`cta-${i}`} style={{
                  background: tier.highlight ? INK : SURF,
                  padding: '16px 22px',
                  borderRight: i < TIERS.length - 1 && !tier.highlight ? `1px solid ${RULE}` : 'none',
                  borderLeft: i > 0 && TIERS[i - 1].highlight ? `1px solid ${RULE}` : 'none',
                }}>
                  <Link to={ctaTarget(tier, isAuth)} style={tier.highlight ? {
                    display: 'block', textAlign: 'center',
                    background: ACC, color: '#fff',
                    fontWeight: 600, fontSize: 14,
                    padding: '11px 16px', borderRadius: 10,
                    textDecoration: 'none',
                    boxShadow: '0 4px 0 0 rgba(0,0,0,0.25)',
                  } : {
                    display: 'block', textAlign: 'center',
                    background: 'transparent', color: INK,
                    border: `1px solid ${INK}`,
                    fontWeight: 600, fontSize: 14,
                    padding: '11px 16px', borderRadius: 10,
                    textDecoration: 'none',
                  }}>{tier.cta}</Link>
                </div>
              ))}

              {/* ── Divider row ── */}
              {TIERS.map((tier, i) => (
                <div key={`div-${i}`} style={{
                  background: tier.highlight ? INK : SURF,
                  borderTop: `1px solid ${tier.highlight ? 'rgba(255,255,255,0.12)' : RULE}`,
                  borderRight: i < TIERS.length - 1 && !tier.highlight ? `1px solid ${RULE}` : 'none',
                  borderLeft: i > 0 && TIERS[i - 1].highlight ? `1px solid ${RULE}` : 'none',
                  padding: '8px 0 0',
                }} />
              ))}

              {/* ── Feature rows ── */}
              {Array.from({ length: 9 }).flatMap((_, fi) =>
                TIERS.map((tier, i) => {
                  const f = tier.features[fi];
                  const isLast = fi === 8;
                  return (
                    <div key={`f${fi}-${i}`} style={{
                      background: tier.highlight ? INK : SURF,
                      padding: `4px 22px ${isLast ? 28 : 4}px`,
                      borderRight: i < TIERS.length - 1 && !tier.highlight ? `1px solid ${RULE}` : 'none',
                      borderLeft: i > 0 && TIERS[i - 1].highlight ? `1px solid ${RULE}` : 'none',
                      borderRadius: isLast ? (i === 0 ? '0 0 0 19px' : i === TIERS.length - 1 ? '0 0 19px 0' : 0) : 0,
                      overflow: isLast && i !== 1 ? 'hidden' : 'visible',
                    }}>
                      <div style={{
                        display: 'flex', gap: 8, alignItems: 'flex-start',
                        fontSize: 13,
                        color: f.included
                          ? (tier.highlight ? 'rgba(236,238,243,0.85)' : INK2)
                          : (tier.highlight ? 'rgba(236,238,243,0.3)' : INK3),
                      }}>
                        <span style={{
                          width: 14, height: 14, flexShrink: 0, marginTop: 3,
                          borderRadius: 4,
                          background: f.included ? ACC : 'transparent',
                          boxShadow: f.included ? 'none' : `inset 0 0 0 1px ${RULE}`,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          fontSize: 9, color: '#fff', fontWeight: 700,
                        }}>
                          {f.included ? '✓' : ''}
                        </span>
                        {f.text}
                      </div>
                    </div>
                  );
                })
              )}

            </div>
          </div>
        )}

        <div style={{
          marginTop: 20,
          display: 'flex', justifyContent: 'center', gap: 24,
          fontSize: 13, color: INK2, flexWrap: 'wrap',
        }}>
          <span>Cancel any time</span>
          <span style={{ color: INK3, fontFamily: '"JetBrains Mono", monospace' }}>·</span>
          <span>All prices excl. VAT</span>
        </div>
      </div>

      {/* FAQ */}
      <div style={{
        background: SURF,
        borderTop: `1px solid ${RULE}`,
        padding: isMobile ? '56px 24px' : '80px 24px',
      }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: isMobile ? 'block' : 'grid', gridTemplateColumns: '1fr 1.5fr', gap: 56 }}>
          <div style={{ marginBottom: isMobile ? 40 : 0 }}>
            <Kicker>FAQ</Kicker>
            <h2 style={{ fontSize: isMobile ? 28 : 40, fontWeight: 700, letterSpacing: '-0.02em', color: INK, marginBottom: 16 }}>
              Questions, answered.
            </h2>
            <p style={{ fontSize: 16, color: INK2 }}>
              Can't find what you need?{' '}
              <a href="mailto:sales@jyto.eu" style={{ color: INK, borderBottom: `1px solid ${INK}` }}>Talk to us</a>.
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {FAQ.map((item, i) => (
              <details key={item.q} open={i === 0} style={{
                border: `1px solid ${RULE}`,
                borderRadius: 12,
                background: BG,
                overflow: 'hidden',
              }}>
                <summary style={{
                  listStyle: 'none',
                  cursor: 'pointer',
                  padding: '16px 18px',
                  fontWeight: 600, fontSize: 15, color: INK,
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  {item.q}
                  <span style={{ fontSize: 20, color: INK3, fontWeight: 400, marginLeft: 12, flexShrink: 0 }}>+</span>
                </summary>
                <p style={{ margin: 0, padding: '0 18px 18px', color: INK2, fontSize: 14, lineHeight: 1.65 }}>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* Trust footer */}
      <div style={{
        background: BG,
        borderTop: `1px solid ${RULE}`,
        padding: '32px 24px',
        textAlign: 'center',
      }}>
        <p style={{ fontSize: 13, color: INK3, fontFamily: '"JetBrains Mono", monospace', maxWidth: 560, margin: '0 auto' }}>
          All plans include GDPR-compliant data processing within the EU.
          No hidden fees. Cancel anytime.
        </p>
      </div>

      <Footer />
    </div>
  );
}
